import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import Navbar from '../components/nav/Navbar';
import { useDropDetail } from '../hooks/useDropDetail';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import PWYWModal from '../components/portal/PWYWModal';
import ShareModal from '../components/common/ShareModal';
import LoadingScreen from '../components/common/LoadingScreen';
import LimitedDropCountdown from '../components/drops/LimitedDropCountdown';

const {
  FiArrowLeft, FiLock, FiPlay, FiPause, FiUnlock,
  FiCheck, FiMusic, FiShare2, FiHeart,
  FiGift, FiLoader, FiUser,
} = FiIcons;

const DropDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { playTrack, currentTrack, isPlaying, togglePlay } = usePlayer();
  const { drop, tiers, tracks, isUnlocked, loading, error, unlockDrop } = useDropDetail(id, user?.id);

  const [selectedTier, setSelectedTier] = useState(null);
  const [purchasing, setPurchasing]     = useState(false);
  const [liked, setLiked]               = useState(false);
  const [showPWYW, setShowPWYW]         = useState(false);
  const [showShare, setShowShare]       = useState(false);
  const [showGift, setShowGift]         = useState(false);
  const [giftEmail, setGiftEmail]       = useState('');
  const [gifted, setGifted]             = useState(false);

  const activeTier = selectedTier || tiers[0] || null;

  if (loading) return <LoadingScreen message="Loading drop…" />;
  if (error || !drop) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
        <div className="text-center space-y-4">
          <p className="text-zinc-500 font-black uppercase tracking-widest">Drop not found</p>
          <Link to="/discover" className="px-6 py-3 btn-brand rounded-2xl text-xs">Back to Discover</Link>
        </div>
      </div>
    );
  }

  const handlePurchase = async () => {
    if (!user) { navigate('/auth'); return; }
    setPurchasing(true);
    await unlockDrop(activeTier?.price || 0);
    setPurchasing(false);
  };

  const handlePlay = (track) => {
    if (!isUnlocked && !track.is_free) return;
    if (currentTrack?.id === track.id) { togglePlay(); return; }
    playTrack(track, { cover: drop.cover_url, artist: drop.artist?.name, album: drop.title });
  };

  const handleGift = () => {
    if (!giftEmail) return;
    setTimeout(() => { setGifted(true); setShowGift(false); }, 800);
  };

  const handleDownload = () => {
    // Placeholder — wire to actual download URL
    console.info('[DropDetail] download triggered for drop:', drop.id);
  };

  const isCurrentlyPlaying = (track) => currentTrack?.id === track.id && isPlaying;
  const isLimited = !!(drop.expires_at || drop.limited_quantity);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink  w-[600px] h-[600px] top-[-20%] right-[-15%] animate-caustic" />
        <div className="ambient-orb-coral w-[400px] h-[400px] bottom-[-10%] left-[-10%] animate-caustic" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-24">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group mb-10">
          <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
            <SafeIcon icon={FiArrowLeft} className="text-sm" />
          </div>
          <span className="text-xs font-black uppercase tracking-widest">Back</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left */}
          <div className="lg:col-span-7 space-y-8">
            <div className="relative aspect-square rounded-3xl overflow-hidden border border-white/8 shadow-2xl">
              {drop.cover_url
                ? <img src={drop.cover_url} alt={drop.title} className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
                    <SafeIcon icon={FiMusic} className="text-6xl text-zinc-700" />
                  </div>
              }
              {!isUnlocked && (
                <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center">
                  <div className="w-20 h-20 glass-strong rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiLock} className="text-3xl text-white/50" />
                  </div>
                </div>
              )}
              {isLimited && (
                <div className="absolute top-4 right-4 px-3 py-1 bg-amber-500/90 backdrop-blur text-[10px] font-black tracking-widest text-black border border-amber-400/50 rounded-lg">
                  ⏳ LIMITED
                </div>
              )}
              {drop.tags?.length > 0 && (
                <div className="absolute top-4 left-4 flex gap-2">
                  {drop.tags.slice(0, 2).map(tag => (
                    <span key={tag} className="px-2 py-1 bg-zinc-950/80 backdrop-blur text-[10px] font-black tracking-widest text-brand-pink border border-brand-pink/30 rounded-lg">{tag}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Tracklist */}
            {tracks.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xs font-black uppercase tracking-widest text-zinc-500 border-b border-white/5 pb-4">
                  Tracklist · {tracks.length} Track{tracks.length !== 1 ? 's' : ''}
                </h3>
                <div className="space-y-2">
                  {tracks.map((track, i) => {
                    const locked  = !isUnlocked && !track.is_free;
                    const playing = isCurrentlyPlaying(track);
                    return (
                      <div key={track.id} onClick={() => handlePlay(track)}
                        className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${
                          locked  ? 'border-transparent bg-zinc-900/40 opacity-50 cursor-not-allowed'
                          : playing ? 'border-brand-pink/30 bg-brand-pink/5 cursor-pointer'
                          : 'glass-card cursor-pointer hover:border-brand-pink/20'
                        }`}>
                        <div className="flex items-center gap-4">
                          <span className="text-zinc-700 font-mono text-sm w-5 text-right">{i + 1}</span>
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${playing ? 'bg-gradient-to-br from-brand-pink to-brand-coral' : 'glass-subtle'}`}>
                            <SafeIcon icon={locked ? FiLock : playing ? FiPause : FiPlay}
                              className={`text-sm ${playing ? 'text-white' : 'text-zinc-500'}`} />
                          </div>
                          <span className={`font-bold text-sm ${locked ? 'blur-[3px] select-none' : 'text-white'}`}>
                            {track.title}
                          </span>
                        </div>
                        <span className="text-zinc-600 font-mono text-xs">{track.duration}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              {drop.artist && (
                <Link to={`/${drop.artist.slug}`} className="flex items-center gap-3 group w-fit">
                  <div className="w-8 h-8 rounded-full overflow-hidden border border-white/10 bg-zinc-900 flex-shrink-0">
                    {drop.artist.avatar_url
                      ? <img src={drop.artist.avatar_url} alt={drop.artist.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><SafeIcon icon={FiUser} className="text-xs text-zinc-600" /></div>
                    }
                  </div>
                  <p className="text-xs font-black tracking-[0.2em] text-brand-pink uppercase group-hover:text-white transition-colors">
                    {drop.artist.name}
                  </p>
                </Link>
              )}

              <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white leading-none">
                {drop.title}
              </h1>
              {drop.description && (
                <p className="text-zinc-400 font-bold text-sm leading-relaxed">{drop.description}</p>
              )}

              <div className="flex gap-3">
                <button onClick={() => setLiked(l => !l)}
                  className={`w-10 h-10 glass-card rounded-xl flex items-center justify-center transition-colors ${liked ? 'text-brand-pink border-brand-pink/30' : 'text-zinc-500 hover:text-brand-pink'}`}>
                  <SafeIcon icon={FiHeart} className="text-sm" />
                </button>
                <button onClick={() => setShowShare(true)}
                  className="w-10 h-10 glass-card rounded-xl flex items-center justify-center text-zinc-500 hover:text-white transition-colors">
                  <SafeIcon icon={FiShare2} className="text-sm" />
                </button>
                <button onClick={() => setShowGift(true)}
                  className="w-10 h-10 glass-card rounded-xl flex items-center justify-center text-zinc-500 hover:text-brand-coral transition-colors">
                  <SafeIcon icon={FiGift} className="text-sm" />
                </button>
              </div>
              {gifted && <p className="text-xs font-black uppercase tracking-widest text-green-400">✓ Gift sent!</p>}
            </div>

            {/* Unlock / Unlocked Panel */}
            {!isUnlocked ? (
              <div className="glass-card rounded-3xl p-6 space-y-6">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiUnlock} className="text-brand-pink" />
                  <h3 className="text-xs font-black uppercase tracking-widest text-white">
                    {tiers.length > 0 ? 'Select Access Tier' : 'Unlock Drop'}
                  </h3>
                </div>

                {tiers.length > 0 ? (
                  <div className="space-y-3">
                    {tiers.map(tier => (
                      <div key={tier.id} onClick={() => setSelectedTier(tier)}
                        className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                          activeTier?.id === tier.id
                            ? 'border-brand-pink/50 bg-brand-pink/5'
                            : 'border-white/5 hover:border-white/10 bg-zinc-900/40'
                        }`}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-black text-sm uppercase tracking-tight text-white">{tier.name}</span>
                          <span className="font-black text-lg text-brand-pink">${tier.price}</span>
                        </div>
                        {tier.perks?.length > 0 && (
                          <ul className="space-y-1">
                            {tier.perks.map((perk, i) => (
                              <li key={i} className="flex items-center gap-2 text-xs text-zinc-400 font-bold">
                                <SafeIcon icon={FiCheck} className="text-brand-pink text-[10px] flex-shrink-0" />{perk}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 rounded-2xl border border-white/5 bg-zinc-900/40 text-center">
                    <p className="font-black text-2xl text-brand-pink">${drop.min_price || 0}</p>
                    <p className="text-xs font-black uppercase tracking-widest text-zinc-500 mt-1">One-time unlock</p>
                  </div>
                )}

                {/* Limited countdown inside unlock panel */}
                {isLimited && (
                  <LimitedDropCountdown
                    dropId={drop.id}
                    isUnlocked={false}
                    onDownload={handleDownload}
                  />
                )}

                <div className="space-y-2">
                  <button onClick={handlePurchase} disabled={purchasing}
                    className="w-full py-5 btn-brand rounded-2xl text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {purchasing
                      ? <><SafeIcon icon={FiLoader} className="animate-spin" /> Processing…</>
                      : `Pay $${activeTier?.price ?? drop.min_price ?? 0} to Unlock`
                    }
                  </button>
                  <button onClick={() => setShowPWYW(true)}
                    className="w-full py-3 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all">
                    Pay What You Want
                  </button>
                </div>
                <p className="text-center text-[10px] font-black uppercase tracking-widest text-zinc-700">
                  100% goes directly to the artist
                </p>
              </div>
            ) : (
              <div className="glass-card rounded-3xl p-8 text-center space-y-5 border-brand-pink/20">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-pink to-brand-coral rounded-2xl flex items-center justify-center mx-auto">
                  <SafeIcon icon={FiCheck} className="text-2xl text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-black uppercase tracking-tight gradient-text">Access Unlocked</h3>
                  <p className="text-zinc-400 font-bold text-sm mt-2">You have full access to this drop.</p>
                </div>

                {/* Countdown or standard download for unlocked users */}
                {isLimited ? (
                  <LimitedDropCountdown
                    dropId={drop.id}
                    isUnlocked={true}
                    onDownload={handleDownload}
                  />
                ) : (
                  <button onClick={handleDownload}
                    className="flex items-center gap-2 mx-auto px-6 py-3 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all">
                    <SafeIcon icon={FiCheck} className="text-sm" /> Download
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <PWYWModal
        isOpen={showPWYW}
        onClose={() => setShowPWYW(false)}
        itemTitle={drop.title}
        suggestedPrice={activeTier?.price || drop.suggested_price || 5}
        onConfirm={async (amount) => {
          const { error } = await unlockDrop(amount);
          if (!error) setShowPWYW(false);
          return { error };
        }}
      />

      {showShare && <ShareModal drop={drop} onClose={() => setShowShare(false)} />}

      {showGift && (
        <div className="modal-overlay" onClick={() => setShowGift(false)}>
          <div className="glass-strong rounded-[2rem] p-8 w-full max-w-md space-y-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-2xl font-black uppercase tracking-tight text-white">Gift This Drop</h3>
            <p className="text-zinc-400 font-bold text-sm">Send access to a friend via email.</p>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Recipient Email</label>
              <input type="email" value={giftEmail} onChange={e => setGiftEmail(e.target.value)}
                className="input-glass" placeholder="friend@example.com" />
            </div>
            <div className="space-y-2">
              <button onClick={handleGift} disabled={!giftEmail}
                className="w-full py-4 btn-brand rounded-2xl text-sm disabled:opacity-50">
                Gift for ${activeTier?.price || drop.min_price || 0}
              </button>
              <button onClick={() => setShowGift(false)}
                className="w-full py-3 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropDetail;